import React from "react";
import DataGrid from "../../app/grid/DataGrid";
import { TBoFormVersionPresentationModel } from "../../../../presenter/applicants/model/BoFormVersionPresentationModel";
import TickBox from "../../app/grid/TickBox";
import { Spin, Icon, Tooltip } from "antd";
import { TReportingPeriodFormTypePresentationModel } from "../../../../model/form/mappers/ReportingPeriodFormTypeListMapper";
import { TApplicantReportingPeriodPresentationModel } from "../../../../presenter/applicants/model/ApplicantReportingPeriodPresentationModel";
import { TGridAction } from "../../app/grid/GridActionsDropdown";
import { IRegisteredApplicant } from "../../../../model/applicant/RegisteredApplicant";
import { StatusForm } from "../../../../common/Constants";
import { IConfiguration } from "../../../../model/config/Configuration";

export interface Props {
  formVersions: TBoFormVersionPresentationModel[];
  translate: any;
  onExpandFormVersion?: any;
  childrenLoader?: boolean;
  children?: any;
  reportingPeriod?: TApplicantReportingPeriodPresentationModel;
  formType?: TReportingPeriodFormTypePresentationModel;
  onChangeModalVisible?: any;
  applicant?: IRegisteredApplicant;
  isApplicantForms?: boolean;
  isApplicantEvaluations?: boolean;
  parentExpand?: boolean;
  configuration: IConfiguration;
}

export interface State {}

export default class FormVersionsGrid extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      formVersions: []
    };
  }

  render() {
    const {
      formVersions,
      translate,
      onExpandFormVersion,
      childrenLoader,
      children,
      onChangeModalVisible,
      applicant,
      reportingPeriod,
      formType,
      isApplicantForms = false,
      isApplicantEvaluations = false,
      parentExpand,
      configuration
    } = this.props;

    const columns = [
      {
        title: translate("applicantEvaluation_page.form_version.status"),
        dataIndex: "status",
        key: "status",
        width: "10%",
        render: (text, record) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            {record.digitallySigned ? (
              <Tooltip
                title={translate(
                  "applicantEvaluation_page.form_version.digital_signature_info",
                  { date: record.signatureDateString }
                )}
              >
                <Icon
                  type="safety-certificate"
                  style={{ marginRight: 8, color: "green", flexShrink: 0 }}
                />
              </Tooltip>
            ) : (
              <span style={{ width: 22, flexShrink: 0 }} />
            )}
            <span>{text}</span>
          </div>
        )
      },
      {
        title: translate("applicantEvaluation_page.form_version.version"),
        dataIndex: "version",
        key: "version"
      },
      {
        title: translate("applicantEvaluation_page.form_version.sender"),
        dataIndex: "sender",
        key: "sender"
      },
      {
        title: translate("applicantEvaluation_page.form_version.formal_check"),
        dataIndex: "formalCheckStatus",
        key: "formalCheckStatus",
        render: (text, record) =>
          record.formalCheckStatus !== undefined ? (
            <TickBox value={record.formalCheckStatus} />
          ) : (
            <div />
          ),
        width: "10%"
      },
      {
        title: translate(
          "applicantEvaluation_page.form_version.for_publication"
        ),
        dataIndex: "forPublication",
        key: "forPublication",
        render: (text, record) => <TickBox value={record.forPublication} />,
        width: "10%"
      },
      {
        title: translate("applicantEvaluation_page.form_version.send_date"),
        dataIndex: "sendDate",
        key: "sendDate",
        sorter: (a, b) =>
          DataGrid.SortHelper.compareDateString(a.sendDate, b.sendDate)
      },
      {
        title: translate(
          "applicantEvaluation_page.form_version.publication_date"
        ),
        dataIndex: "publicationDate",
        key: "publicationDate",
        sorter: (a, b) =>
          DataGrid.SortHelper.compareDateString(
            a.publicationDate,
            b.publicationDate
          )
      },
      {
        title: translate(
          "applicantEvaluation_page.form_version.number_item_electronic_registry"
        ),
        dataIndex: "numberItemElectronicRegistry",
        key: "numberItemElectronicRegistry"
      },
      {
        title: translate(
          "applicantEvaluation_page.form_version.sub_number_item_electronic_registry"
        ),
        dataIndex: "subNumberItemElectronicRegistry",
        key: "subNumberItemElectronicRegistry"
      }
    ];

    const expandedRowRender = (e) => {
      return <Spin spinning={childrenLoader ? true : false}>{children}</Spin>;
    };

    const getActions = () => {
      let formVersionActions: TGridAction[] = [];

      let action = {
        label: translate("applicantForms_page.details"),
        onClick: (record) => (e) =>
          window.open(
            `${configuration.frontOfficeUrl}/reporting-periods/${reportingPeriod?.id}/forms/${formType?.id}/details?version=${record.version}&applicantId=${applicant?.id}`,
            "_blank"
          ),
        type: "primary"
      } as TGridAction;

      let formForAnnouncementAction = {
        label: translate(
          "applicantForms_page.actions.set_form_for_announcement"
        ),
        type: "default",
        onClick: (record) => (e) => {
          onChangeModalVisible("announcement", true, record);
        },
        enable: (record) => {
          return record.status === StatusForm.DRAFT ? false : true;
        }
      } as TGridAction;

      let updateNumberElectronicRegistry = {
        label: translate(
          "applicantForms_page.actions.update_number_electronic_registry"
        ),
        type: "default",
        onClick: (record) => (e) =>
          onChangeModalVisible("electronicRegistry", true, record)
      } as TGridAction;

      let applicantEvaluationsAction = {
        label: translate("applicantForms_page.actions.initiate_new_check"),
        type: "default",
        onClick: (record) => (e) => {
          onChangeModalVisible("newEvaluation", true, record);
        },
        enable: (record) => {
          return !(record.status === StatusForm.DRAFT);
        }
      } as TGridAction;

      let formCheckStatusAction = {
        label: translate("applicantForms_page.actions.set_formal_check_status"),
        type: "default",
        onClick: (record) => (e) => {
          onChangeModalVisible("formalCheck", true, record);
        },
        enable: (record) => {
          return record.status === StatusForm.DRAFT ? false : true;
        }
      } as TGridAction;

      let downloadSignedReportAction = {
        label: translate("applicantForms_page.actions.download_signed_report"),
        type: "default",
        onClick: (record) => () =>
          window.open(`/api/forms/${record.id}/signed-report`, "_blank"),
        enable: (record) => record.digitallySigned
      } as TGridAction;

      if (reportingPeriod?.hasRightOfPreview && isApplicantForms) {
        formVersionActions.push(action);
      }

      if (isApplicantForms) {
        formVersionActions.push(formCheckStatusAction);
        formVersionActions.push(formForAnnouncementAction);

        formVersionActions.push(updateNumberElectronicRegistry);
        formVersionActions.push(downloadSignedReportAction);

        if (reportingPeriod?.hasRightOfPreview) {
          formVersionActions.push(action);
        }
      }

      if (isApplicantEvaluations) {
        formVersionActions.push(applicantEvaluationsAction);
      }

      return formVersionActions;
    };

    return (
      <DataGrid
        title={translate("applicantEvaluation_page.form_version.title")}
        translate={translate}
        columns={columns}
        dataSource={formVersions}
        booleanColumnKeys={["status", "forPublication", "formalCheckStatus"]}
        expandedRowRender={onExpandFormVersion && expandedRowRender}
        singleRowExpandEnabled={onExpandFormVersion ? true : false}
        onExpand={onExpandFormVersion}
        actions={getActions()}
        horizontalScroll={false}
        expandedRowKeyList={!parentExpand ? [] : undefined}
      />
    );
  }
}
